/**
 * LandscapeView — Grid of subject cards showing pending work.
 *
 * The "home screen" for frontline staff: see all subjects with work counts,
 * click one to focus on their tasks. Subjects with 0 pending tasks shown
 * with "Done" badge but remain visible for quick access.
 *
 * DRY: Uses subjectLabel from settings for industry-appropriate terminology.
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  TextInput,
  Group,
  SegmentedControl,
  Stack,
  Alert,
  Skeleton,
  Badge,
  Paper,
} from '@mantine/core';
import { IconSearch, IconAlertCircle, IconUsers } from '@tabler/icons-react';
import { SubjectCard, type TaskCounts } from './SubjectCard';

export interface SubjectWithCounts {
  id: string;
  name: string;
  taskCounts: TaskCounts;
  avatarUrl?: string;
}

export interface LandscapeViewProps {
  readonly subjects: SubjectWithCounts[];
  readonly loading: boolean;
  readonly error?: string;
  readonly subjectLabel: string;
  readonly onSelectSubject: (id: string) => void;
}

type FilterMode = 'all' | 'pending' | 'done';

export function LandscapeView({
  subjects,
  loading,
  error,
  subjectLabel,
  onSelectSubject,
}: LandscapeViewProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('pending');

  // Aggregate stats
  const stats = useMemo(() => {
    const total = subjects.length;
    const withPending = subjects.filter((s) => s.taskCounts.total - s.taskCounts.completed > 0).length;
    const withCritical = subjects.filter((s) => s.taskCounts.critical > 0).length;
    const withOverdue = subjects.filter((s) => s.taskCounts.overdue > 0).length;
    const totalTasks = subjects.reduce((sum, s) => sum + s.taskCounts.total - s.taskCounts.completed, 0);
    return { total, withPending, withCritical, withOverdue, totalTasks };
  }, [subjects]);

  // Filter and search
  const filteredSubjects = useMemo(() => {
    let result = [...subjects];

    // Apply filter
    if (filter === 'pending') {
      result = result.filter((s) => s.taskCounts.total - s.taskCounts.completed > 0);
    } else if (filter === 'done') {
      result = result.filter((s) => s.taskCounts.total - s.taskCounts.completed === 0 && s.taskCounts.total > 0);
    }

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(searchLower));
    }

    // Sort: critical first, then by pending count descending
    result.sort((a, b) => {
      if (a.taskCounts.critical !== b.taskCounts.critical) {
        return b.taskCounts.critical - a.taskCounts.critical;
      }
      const aPending = a.taskCounts.total - a.taskCounts.completed;
      const bPending = b.taskCounts.total - b.taskCounts.completed;
      return bPending - aPending;
    });

    return result;
  }, [subjects, filter, search]);

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error}
      </Alert>
    );
  }

  const pluralLabel = subjectLabel.toLowerCase() + 's';

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={2}>My {pluralLabel.charAt(0).toUpperCase() + pluralLabel.slice(1)}</Title>
            <Text c="dimmed" size="sm">
              {stats.totalTasks} task{stats.totalTasks !== 1 ? 's' : ''} across {stats.withPending}{' '}
              {pluralLabel}
            </Text>
          </div>

          {stats.withCritical > 0 && (
            <Badge color="red" size="lg" variant="filled">
              {stats.withCritical} with critical tasks
            </Badge>
          )}
        </Group>

        {/* Filters */}
        <Paper withBorder p="sm">
          <Group justify="space-between">
            <TextInput
              placeholder={`Search ${pluralLabel}...`}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: 300 }}
            />

            <SegmentedControl
              value={filter}
              onChange={(value) => setFilter(value as FilterMode)}
              data={[
                { value: 'pending', label: `Pending (${stats.withPending})` },
                { value: 'done', label: 'Done' },
                { value: 'all', label: 'All' },
              ]}
            />
          </Group>
        </Paper>

        {/* Loading state */}
        {loading && (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={100} radius="md" />
            ))}
          </SimpleGrid>
        )}

        {/* Empty state */}
        {!loading && filteredSubjects.length === 0 && (
          <Paper withBorder p="xl" ta="center">
            <IconUsers size={48} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed" mt="md">
              {search ? `No ${pluralLabel} match "${search}"` : `No ${pluralLabel} with ${filter} tasks`}
            </Text>
          </Paper>
        )}

        {/* Subject grid */}
        {!loading && filteredSubjects.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
            {filteredSubjects.map((subject) => (
              <SubjectCard
                key={subject.id}
                id={subject.id}
                name={subject.name}
                taskCounts={subject.taskCounts}
                onClick={onSelectSubject}
                avatarUrl={subject.avatarUrl}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Container>
  );
}
